import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { Pagination } from '../components/Pagination';
import { ThemedSelect } from '../components/ThemedSelect';
import { RequireCustomer } from '../../lib/use-customer-auth';
import {
  customerApiErrorMessage,
  getMyWallet,
  listMyWalletTransactions,
  type WalletTransactionRow,
} from '../../lib/customer-api';
import { toast } from '../../lib/toast';

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

const formatDateTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const transactionLabel: Record<WalletTransactionRow['type'], string> = {
  CREDIT_GIFT_CARD: 'Gift card credit',
  DEBIT_BOOKING: 'Booking payment',
  REFUND_BOOKING: 'Booking refund',
  EXPIRY: 'Balance expired',
  ADMIN_ADJUSTMENT: 'Admin adjustment',
};

export default function WalletPage() {
  return (
    <RequireCustomer>
      <WalletInner />
    </RequireCustomer>
  );
}

const ACTIVITY_PAGE_SIZE = 10;

function WalletInner() {
  const [balance, setBalance] = useState(0);
  const [currency, setCurrency] = useState('GBP');
  const [history, setHistory] = useState<WalletTransactionRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Activity list paginates independently of the wallet summary so the
  // page header / balance don't reload when you flip pages.
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);
  // Credit / debit filter for the activity feed.
  const [direction, setDirection] = useState<'all' | 'credit' | 'debit'>(
    'all',
  );

  // Guards the [activityPage, direction] effect from double-firing on
  // mount. We deliberately load activity from inside `load()` AFTER
  // getMyWallet returns, because getMyWallet runs the reconcile pass
  // that writes the missing CREDIT_GIFT_CARD ledger row for any
  // pre-assigned gift cards. Querying the activity feed in parallel
  // would race the reconcile commit and show "No activity yet" while
  // the balance jumps to £50.
  const bootstrapped = useRef(false);

  const loadActivity = async (page: number) => {
    setActivityLoading(true);
    try {
      const txs = await listMyWalletTransactions({
        limit: ACTIVITY_PAGE_SIZE,
        offset: (page - 1) * ACTIVITY_PAGE_SIZE,
        ...(direction === 'all' ? {} : { direction }),
      });
      setHistory(txs.data ?? []);
      setActivityTotal(txs.total_count ?? 0);
    } catch {
      // Non-fatal — the summary still renders; surface nothing extra.
    } finally {
      setActivityLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const wallet = await getMyWallet();
      setBalance(Number(wallet.balance ?? 0));
      setCurrency(wallet.currency ?? 'GBP');
      await loadActivity(1);
    } catch (err) {
      const msg = customerApiErrorMessage(err, "Couldn't load your wallet.");
      toast.error(msg);
    } finally {
      setLoading(false);
      bootstrapped.current = true;
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!bootstrapped.current) return;
    loadActivity(activityPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityPage, direction]);

  const activityTotalPages = Math.max(
    1,
    Math.ceil(activityTotal / ACTIVITY_PAGE_SIZE),
  );

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-8 md:py-12">
        <div className="flex items-start gap-3 sm:gap-4 mb-8 md:mb-10">
          <Link
            to="/account-settings"
            className="mt-1 w-[40px] h-[40px] bg-[#3a3335]/[0.04] rounded-[10px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors shrink-0"
          >
            <span className="material-icons text-[#908e8e] text-[18px]">
              chevron_left
            </span>
          </Link>
          <div>
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] mb-1">
              Wallet
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              Your gift card balance and recent activity
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-center text-[#908e8e] text-[14px] py-12">
            Loading your wallet…
          </p>
        ) : (
          <div className="flex flex-col gap-8">
            {/* Balance hero card */}
            <div className="rounded-2xl border border-[#ede8e9] bg-[#fcfaf7] px-6 py-8 sm:px-10 sm:py-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] tracking-[-0.1px] uppercase">
                  Current balance
                </p>
                <p className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[40px] sm:text-[52px] leading-none mt-2">
                  {formatMoney(balance, currency)}
                </p>
                <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[13px] mt-2">
                  Apply at booking checkout — used first before any card
                  payment.
                </p>
              </div>
              <Link
                to="/gift-card"
                className="self-start sm:self-auto inline-flex items-center gap-1.5 rounded-[10px] bg-white border border-[#ede8e9] px-4 py-2 text-[13px] font-bold text-[#8e2a2f] hover:bg-[#fff5f3] transition-colors"
              >
                <span className="material-icons text-[16px]">redeem</span>
                Send a gift card
              </Link>
            </div>

            {/* History feed */}
            <section>
              <div className="flex items-center justify-between mb-3 gap-3">
                <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px]">
                  Activity
                </h2>
                <ThemedSelect<'all' | 'credit' | 'debit'>
                  value={direction}
                  onChange={(v) => {
                    setDirection(v);
                    setActivityPage(1);
                  }}
                  options={[
                    { value: 'all', label: 'All activity' },
                    { value: 'credit', label: 'Credits' },
                    { value: 'debit', label: 'Debits' },
                  ]}
                />
              </div>
              {history.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#eee] py-10 text-center">
                  <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-bold">
                    No activity yet
                  </p>
                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mt-1">
                    Credits and bookings will appear here as they happen.
                  </p>
                </div>
              ) : (
                <ul
                  className={`flex flex-col transition-opacity ${
                    activityLoading ? 'opacity-50' : ''
                  }`}
                >
                  {history.map((row) => {
                    const isCredit = row.amount >= 0;
                    return (
                      <li
                        key={row.id}
                        className="flex items-center justify-between gap-3 py-3 border-b border-[#eee] last:border-b-0"
                      >
                        <div className="min-w-0">
                          <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] truncate">
                            {row.description || transactionLabel[row.type]}
                          </p>
                          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[11px]">
                            {formatDateTime(row.created_at)}
                            {row.gift_card ? ` · ${row.gift_card.code}` : ''}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p
                            className={`font-['DM_Sans',sans-serif] font-bold text-[14px] ${
                              isCredit ? 'text-[#198754]' : 'text-[#8e2a2f]'
                            }`}
                          >
                            {isCredit ? '+' : '−'}
                            {formatMoney(Math.abs(row.amount), row.currency)}
                          </p>
                          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[11px]">
                            Balance{' '}
                            {formatMoney(row.balance_after, row.currency)}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {activityTotalPages > 1 ? (
                <div className="flex justify-center mt-6">
                  <Pagination
                    currentPage={activityPage}
                    totalPages={activityTotalPages}
                    onPageChange={setActivityPage}
                  />
                </div>
              ) : null}
            </section>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
